import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service | Tablecure";
  }, []);

  const lastUpdated = "April 12, 2025";

  return (
    <div className="policy-page tc-page">
      <div className="pd2-bg">
        <div className="policy-orb policy-orb1" />
        <div className="policy-orb policy-orb2" />
        <div className="ck-grid" />
      </div>

      <div className="policy-inner">
        <div className="policy-header">
          <div className="pd2-eyebrow">
            <span className="pd2-eyebrow-dot" />
            Legal
          </div>
          <h1 className="policy-title">Terms of Service</h1>
          <p className="policy-subtitle">Last updated: {lastUpdated}</p>
        </div>

        <div className="policy-body">

          <section className="policy-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using <strong>tablecure.com</strong> (the "Site") you agree to be bound
              by these Terms of Service and all applicable laws and regulations. If you do not agree
              with any of these terms, you are prohibited from using the Site.
            </p>
            <p>
              We reserve the right to update these terms at any time without notice. Your continued
              use of the Site after any changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. About Tablecure</h2>
            <p>
              Tablecure is an online store specialising in premium desk lamps and ergonomic lighting
              solutions. Our products are designed for engineers, designers, researchers, and
              professionals who demand precision, quality, and long-lasting performance from their
              workspace equipment.
            </p>
          </section>

          <section className="policy-section">
            <h2>3. Account Registration</h2>
            <p>
              To place an order, you may be required to create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration.</li>
              <li>Maintain and promptly update your account information.</li>
              <li>Keep your password confidential and not share it with any third party.</li>
              <li>Notify us immediately at <a href="mailto:support@tablecure.com">support@tablecure.com</a> of any unauthorised use of your account.</li>
            </ul>
            <p>
              We reserve the right to terminate accounts that contain false information or that
              violate these Terms of Service.
            </p>
          </section>

          <section className="policy-section">
            <h2>4. Products and Pricing</h2>
            <p>
              All prices displayed on the Site are in Indian Rupees (₹) and are inclusive of
              applicable taxes unless stated otherwise. We reserve the right to change prices at any
              time without prior notice.
            </p>
            <p>
              Product descriptions, images, and specifications are provided for informational purposes
              and are as accurate as possible. Minor variations in colour or appearance may occur due
              to display settings. We do not warrant that product descriptions or other content on the
              Site is accurate, complete, reliable, or error-free.
            </p>
          </section>

          <section className="policy-section">
            <h2>5. Orders and Payment</h2>
            <p>
              When you place an order, you are making an offer to purchase the product(s) at the
              stated price. We reserve the right to accept or decline any order for any reason,
              including product unavailability or suspected fraud.
            </p>
            <p>
              Payments are processed securely by <strong>Razorpay</strong>. We accept all major
              credit/debit cards, UPI, net banking, and wallets supported by Razorpay. By submitting
              a payment, you represent and warrant that you are authorised to use the designated
              payment method.
            </p>
            <p>
              Order confirmation is sent to your registered email address. This confirmation
              constitutes acceptance of your order.
            </p>
          </section>

          <section className="policy-section">
            <h2>6. Shipping and Delivery</h2>
            <p>
              We ship to addresses across India and internationally. Estimated delivery timelines and
              shipping charges are detailed on our{" "}
              <Link to="/shipping-policy">Shipping &amp; Returns Policy</Link> page. Timelines are
              estimates and are not guaranteed; delays beyond our control (customs, courier delays,
              natural events) are not our liability.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. Returns and Refunds</h2>
            <p>
              We offer a 7-day return policy from the date of delivery. Full details, including
              eligibility criteria and the process for initiating a return, are available on our{" "}
              <Link to="/shipping-policy">Shipping &amp; Returns Policy</Link> page.
            </p>
            <p>
              Refunds for approved returns are processed to the original payment method within
              5–10 business days of receiving the returned product.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. Warranty</h2>
            <p>
              All Tablecure products carry a <strong>2-year manufacturer's warranty</strong> against
              defects in materials and workmanship under normal use. The warranty does not cover:
            </p>
            <ul>
              <li>Damage caused by accident, misuse, or modification.</li>
              <li>Normal wear and tear.</li>
              <li>Damage caused by operating outside the permitted or intended uses described in the documentation.</li>
            </ul>
            <p>
              To claim a warranty, contact us at{" "}
              <a href="mailto:support@tablecure.com">support@tablecure.com</a> with proof of purchase
              and a description of the defect.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Intellectual Property</h2>
            <p>
              All content on the Site — including text, graphics, logos, product images, and software
              — is the property of Tablecure or its content suppliers and is protected by applicable
              intellectual property laws. You may not reproduce, distribute, or create derivative works
              without our express written permission.
            </p>
          </section>

          <section className="policy-section">
            <h2>10. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Site for any unlawful purpose.</li>
              <li>Attempt to gain unauthorised access to any part of the Site or its related systems.</li>
              <li>Transmit any harmful, offensive, or disruptive content.</li>
              <li>Use automated tools to scrape, crawl, or extract data from the Site without permission.</li>
              <li>Impersonate any person or entity, or falsely represent your affiliation with any person or entity.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Tablecure shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, including loss of profits, data,
              goodwill, or other intangible losses, arising from:
            </p>
            <ul>
              <li>Your use of or inability to use the Site or products.</li>
              <li>Any unauthorised access to or alteration of your transmissions or data.</li>
              <li>Any content obtained from the Site.</li>
            </ul>
            <p>
              Our total liability to you for any claim arising out of or relating to these terms shall
              not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="policy-section">
            <h2>12. Governing Law</h2>
            <p>
              These Terms of Service are governed by and construed in accordance with the laws of
              India. Any disputes arising under these terms shall be subject to the exclusive
              jurisdiction of the courts of India.
            </p>
          </section>

          <section className="policy-section">
            <h2>13. Contact</h2>
            <p>
              Questions about these Terms of Service should be sent to:
            </p>
            <div className="policy-contact-block">
              <p><strong>Tablecure</strong></p>
              <p>Email: <a href="mailto:support@tablecure.com">support@tablecure.com</a></p>
              <p>Website: <Link to="/contact">Contact Form</Link></p>
            </div>
          </section>

        </div>

        <div className="policy-footer-nav">
          <Link to="/" className="pd2-btn-ghost">← Back to Home</Link>
          <div className="policy-footer-links">
            <Link to="/privacy-policy" className="policy-footer-link">Privacy Policy</Link>
            <span className="policy-footer-sep" />
            <Link to="/shipping-policy" className="policy-footer-link">Shipping &amp; Returns</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
